import {
	DndProvider,
	type DragLayerMonitorProps,
	type DropOptions,
	type NodeModel,
	Tree,
} from "@minoru/react-dnd-treeview";
import { Box, CircularProgress, Grid2 } from "@mui/material";
import { useMutation, useQuery } from "@tanstack/react-query";
import React, { useEffect, useMemo, useState } from "react";
import { HTML5Backend } from "react-dnd-html5-backend";
import { ErrorModal } from "../../../../../components/modals/error-modal";
import MesfModal from "../../../../../components/modals/modal.mesf";
import { NotificationSnackBar } from "../../../../../controls/snackbars/notification-snackbar";
import { TagSearchResult } from "../hooks/useTagSearch";
import { TagFromTree } from "../models/TrendingModelsV2";
import {
	changeTagFolderToTag,
	getTagsTree,
	upsertTagFolder,
} from "../repository/TrendingRepositoryV2";
import { buildTagsTreeV2 } from "../utils/buildTagsTree";
import { TagFolderCustomNodeV2 } from "./TagFolderCustomNodeV2";
import { TagSearchInput } from "./TagSearchInput";
import { ContextMenu } from "./modals/tagsTreeModal/ContextMenu";
import { CustomDragPreview } from "./modals/tagsTreeModal/CustomDragView";

// React Query hook for fetching tags tree
const useSearchTagsTree = () => {
	return useQuery({
		queryKey: ["tagsTree-v2"] as const,
		queryFn: ({ signal }) => getTagsTree(signal),
		enabled: false, // Only fetch when explicitly called
	});
};

interface Props {
	open: boolean;
	handleClose: () => void;
	onTagSelect: (selected: TagFromTree) => void;
}

export const TagsTreeModalV2 = ({ open, handleClose, onTagSelect }: Props) => {
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState("");
	const [snackbarMessage, setSnackbarMessage] = useState<string | null>(null);

	const [tagsTreeData, setTagsTreeData] = useState<NodeModel<TagFromTree>[]>(
		[],
	);
	const [tagNodeSelectedInTree, setTagNodeSelectedInTree] =
		useState<NodeModel<TagFromTree> | null>(null);
	const [anchorPoint, setAnchorPoint] = useState<{
		x: number;
		y: number;
	} | null>(null);
	const [showContextMenu, setShowContextMenu] = useState(false);

	const backendOffset = 1000000;

	const [openTagFolders, setOpenTagFolders] = useState<{
		[TagId: string]: string | number;
		[TagId: number]: string | number;
	}>({});
	const [selectedNodes, setSelectedNodes] = useState<NodeModel<TagFromTree>[]>(
		[],
	);

	const handleContextMenu = (
		data: NodeModel<TagFromTree>,
		e: React.MouseEvent<HTMLElement>,
	) => {
		e.preventDefault();
		const top = e.clientY;
		const left = e.clientX;

		setAnchorPoint({ x: left, y: top });

		setSelectedNodes([data]);

		setShowContextMenu(true);
		const selectedNode = tagsTreeData.find(
			(node: NodeModel<TagFromTree>) => node.data?.TagId === data.id,
		);
		if (selectedNode) {
			setTagNodeSelectedInTree(selectedNode);
		}
	};

	const {
		data: tagsTree,
		isLoading: tagsTreeLoading,
		isError: tagsTreeIsError,
		error: tagsTreeError,
		isSuccess: tagsTreeIsSuccess,
		isFetched: tagsTreeIsFetched,
		refetch,
	} = useSearchTagsTree();

	const updateTagFolder = useMutation({
		mutationFn: ({
			tagFolderId,
			parentTagFolderId,
			tagFolderName,
			isActive,
		}: {
			tagFolderId: number | null;
			parentTagFolderId: number | null;
			tagFolderName: string;
			isActive: boolean;
		}) =>
			upsertTagFolder({
				tagFolderId,
				parentTagFolderId,
				tagFolderName,
				isActive,
			}),
		onError: (error: Error) => {
			setError(error.message);
		},
	});

	const changeTagFolderTag = useMutation({
		mutationFn: ({
			tagId,
			tagFolderId,
		}: {
			tagId: number | null;
			tagFolderId: number | null;
		}) => changeTagFolderToTag({ tagId, tagFolderId }),
		onError: (error: Error) => {
			setError(error.message);
		},
	});

	const expanded = useMemo(() => {
		return Object.keys(openTagFolders).map((el) => Number.parseInt(el));
	}, [openTagFolders]);

	const handleDrop = async (
		tree: NodeModel<TagFromTree>[],
		options: DropOptions<TagFromTree>,
	) => {
		setIsLoading(true);
		if (options.dragSource) {
			const tagId = Number(options.dragSource.id);
			const newFolderId = Number(options.dropTarget?.id) || null;

			if (tagId === newFolderId) {
				setIsLoading(false);
				return;
			}
			if (options.dragSource.data?.TagTypeCode === "F") {
				await updateTagFolder.mutate({
					tagFolderId: tagId,
					parentTagFolderId: newFolderId,
					tagFolderName: options.dragSource.text,
					isActive: true,
				});
			} else {
				await changeTagFolderTag.mutate({ tagId, tagFolderId: newFolderId });
			}
			setTagsTreeData(tree);
		}
		setIsLoading(false);
	};

	const handleClick = (node: NodeModel<TagFromTree>) => {
		if (node.data && node.data.TagTypeCode !== "F") {
			onTagSelect(node.data);
			// Don't close modal - keep it open for multiple selections
		}
	};

	const handleCloseContextMenu = (e: any) => {
		if (showContextMenu) {
			setShowContextMenu(false);
		}
	};

	const handleSearchResultClick = (result: TagSearchResult) => {
		// Expand path to the tag
		const expandPath = (nodeId: number): number[] => {
			const path: number[] = [];
			const findNodeAndPath = (currentId: number): void => {
				const node = tagsTreeData.find((n) => n.id === currentId);
				if (node && node.parent !== backendOffset) {
					path.unshift(currentId);
					findNodeAndPath(node.parent as number);
				} else if (node) {
					path.unshift(currentId);
				}
			};
			findNodeAndPath(nodeId);
			return path;
		};

		const pathToExpand = expandPath(result.id);

		const newOpenTagFolders = { ...openTagFolders };
		pathToExpand.forEach((nodeId) => {
			newOpenTagFolders[nodeId] = nodeId;
		});

		setOpenTagFolders(newOpenTagFolders);

		setSelectedNodes([result.node]);

		if (result.node.data && result.node.data.TagTypeCode !== "F") {
			onTagSelect(result.node.data);
			setSnackbarMessage(`Tag "${result.node.data.TagName}" added`);
		}

		// Scroll to the node after expansion
		setTimeout(() => {
			const nodeElement = document.querySelector(
				`[data-node-id="${result.id}"]`,
			);
			if (nodeElement) {
				nodeElement.scrollIntoView({
					behavior: "smooth",
					block: "center",
				});
			}
		}, 300);
	};

	useEffect(() => {
		if (open) {
			refetch();
		}
	}, [open]);

	useEffect(() => {
		if (open && tagsTree) {
			let tree = buildTagsTreeV2(tagsTree);

			// Transform parent 0 to backendOffset for root-level nodes
			tree = tree.map((node) => {
				if (node.parent === 0) {
					return { ...node, parent: backendOffset };
				}
				return node;
			});

			// Fix: If a tag's parent doesn't exist in the tree, show it at root level
			const existingIds = new Set(tree.map((node) => node.id));
			tree = tree.map((node) => {
				// If parent doesn't exist in the tree and it's not already at root, move to root
				if (node.parent !== backendOffset && !existingIds.has(node.parent)) {
					return { ...node, parent: backendOffset };
				}
				return node;
			});

			// Filter out any node with id === backendOffset (the root itself shouldn't be visible)
			tree = tree.filter((node) => node.id !== backendOffset);

			// Also filter out any node with id === 0 (legacy root folder)
			tree = tree.filter((node) => node.id !== 0);

			setTagsTreeData(tree);
		}
	}, [tagsTreeIsSuccess, tagsTreeIsFetched, open]);

	useEffect(() => {
		if (tagsTreeError && tagsTreeIsError) {
			setError(
				tagsTreeError instanceof Error
					? tagsTreeError.message
					: String(tagsTreeError),
			);
		}
	}, [tagsTreeError, tagsTreeIsError]);

	return (
		<>
			<MesfModal
				open={open}
				handleClose={handleClose}
				maxWidth={"lg"}
				aria-labelledby="modal-modal-title"
				aria-describedby="modal-modal-description"
				title="Tags"
			>
				<MesfModal.Content>
					<div
						style={{
							display: open ? "block" : "none",
							minHeight: "700px",
							minWidth: "800px",
						}}
						onClick={handleCloseContextMenu}
					>
						<TagSearchInput
							tagNodes={tagsTreeData}
							onSearchResultClick={handleSearchResultClick}
						/>
						<div style={{ overflowY: "auto", maxHeight: "650px" }}>
							{isLoading || tagsTreeLoading ? (
								<Grid2
									container
									justifyContent="center"
									alignItems="center"
									style={{ minHeight: "100px" }}
								>
									<CircularProgress size="2rem" />
								</Grid2>
							) : (
								<DndProvider backend={HTML5Backend}>
									<Box
										sx={{
											"& .root": {
												boxSizing: "border-box",
												height: "100%",
												padding: "32px",
											},
											"& .dropTarget": {
												boxShadow: "0 0 0 1px #1967d2 inset",
												borderRadius: "1px",
												backgroundColor: "#DAE4F2",
											},
										}}
									>
										<Tree
											classes={{
												root: "root",
												dropTarget: "dropTarget",
											}}
											initialOpen={expanded}
											tree={tagsTreeData}
											rootId={backendOffset}
											onDrop={handleDrop}
											canDrop={(
												tag: any,
												options: DropOptions<TagFromTree>,
											): boolean => {
												return !selectedNodes.some(
													(selectedNode) =>
														Number(selectedNode.id) ===
														Number(options.dropTargetId),
												);
											}}
											render={(node, options) => {
												const selected = selectedNodes.some(
													(selectedNode) =>
														Number(selectedNode.id) === Number(node.id),
												);
												return (
													<TagFolderCustomNodeV2
														setContextMenuOver={handleContextMenu}
														node={node}
														options={options}
														isSelected={selected}
														openTagFolders={openTagFolders}
														setOpenTagFolders={setOpenTagFolders}
														handleSelect={handleClick}
													/>
												);
											}}
											dragPreviewRender={(
												monitorProps: DragLayerMonitorProps<TagFromTree>,
											) => <CustomDragPreview monitorProps={monitorProps} />}
										/>
									</Box>
								</DndProvider>
							)}
						</div>
					</div>
					<ContextMenu
						anchorPoint={anchorPoint}
						isOpen={showContextMenu}
						setShowContextMenu={setShowContextMenu}
						tagNodeSelectedInTree={tagNodeSelectedInTree}
						getTagsTreeData={() => refetch()}
					/>
					<ErrorModal error={error} onHide={() => setError("")} />
					<NotificationSnackBar
						message={snackbarMessage}
						onHide={() => setSnackbarMessage(null)}
					/>
				</MesfModal.Content>
			</MesfModal>
		</>
	);
};
